/**
 * ProjectSetup — the client half of the mokulearner-node ⇄ mokulab seam.
 *
 * Three layers, never collapsed (mokunet-org CANON §2 tier table):
 *   inherited  read from the node's facility reference (place, siting verdict,
 *              land-use filing path, goals); never re-asked, never recomputed.
 *   authored   "the thing itself" a partner surface may author: parcel / TMK,
 *              buildings and structures on the parcel, scope of work.
 *   derived    the permit map: one building-permit application per item the
 *              jurisdiction's rules name (permit_applications.json), shown
 *              beside the node's land-use posture.
 *
 * Persisted per viewer in localStorage as a convenience; the governed copy is
 * what BIMSession.apply_project_setup stamps on the IfcProject. Mirrors
 * pipeline/bim/project_setup.py (derive_permit_map) so the panel can show the
 * map live without a round trip; the worker is the authority when generating.
 */

import permitRules from '../../scripts/ifc_worker/pipeline/bim/data/permit_applications.json';

export type Track = 'commercial' | 'residential';

export interface Inherited {
    facility_id?: string | null;
    moku_id?: string | null;
    h3_index?: string | null;
    facility_name?: string | null;
    facility_type?: string | null;
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    structural_system?: string | null;
    siting_verdict?: string | null;
    siting_class?: string | null;
    siting_state_code?: string | null;
    siting_county_codes?: string[];
    siting_in_corridor?: boolean | null;
    ial_priority_eligible?: boolean | null;
    siting_exception?: string | null;
    source?: string;
}

export interface Structure {
    id: string;
    name: string;
    community_type?: string | null;
    kind: 'building' | 'structure';
    new: boolean;
    /** Storeys above grade; drives floor-ceiling applicability in the systems hierarchy. */
    storeys?: number;
}

export interface ProjectSetup {
    project_name: string;
    jurisdiction: string;
    inherited: Inherited;
    parcel_id?: string | null;
    address?: string | null;
    structures: Structure[];
    site_work: boolean;
    demolitions: string[];
    adus: string[];
    track?: Track | null;
    description?: string | null;
}

export interface PermitApplication {
    id: string; kind: string; label: string; subject: string; domain: 'building' | 'landscape';
    sheet_profile: string; number: number; title: string;
}

export interface PermitMap {
    jurisdiction: string; jurisdiction_title?: string; source?: string; track: Track;
    application_type: string; application_count: number; applications: PermitApplication[];
    answers: { id: string; question: string; answer: 'yes' | 'no'; yields: number; note?: string }[];
    warning?: string; contacts: string[];
}

const RESIDENTIAL_TYPES = new Set(['farm_dwelling', 'employee_housing']);
const STORAGE_KEY = 'mokulab.projectSetup.v1';
export const SETUP_EVENT = 'ws-project-setup';

type Rules = typeof permitRules;
type Jurisdiction = Rules['jurisdictions'][keyof Rules['jurisdictions']];

export function jurisdictions(): { id: string; title: string }[] {
    return Object.entries(permitRules.jurisdictions).map(([id, j]) => ({ id, title: (j as { title: string }).title }));
}

export function emptySetup(): ProjectSetup {
    return { project_name: '', jurisdiction: 'honolulu_dpp', inherited: {}, structures: [], site_work: false, demolitions: [], adus: [], track: null };
}

export function effectiveTrack(s: ProjectSetup): Track {
    if (s.track === 'commercial' || s.track === 'residential') return s.track;
    const types = s.structures.map(x => x.community_type).filter((t): t is string => !!t);
    return types.length > 0 && types.every(t => RESIDENTIAL_TYPES.has(t)) ? 'residential' : 'commercial';
}

/** Mirror of pipeline.bim.project_setup.derive_permit_map. */
export function derivePermitMap(s: ProjectSetup): PermitMap {
    const all = permitRules.jurisdictions as Record<string, Jurisdiction>;
    const jr = (all[s.jurisdiction] ?? all.default) as Jurisdiction & { questions: any[]; application_types: { single: string; multiple: string }; warning?: string; contacts?: string[]; source?: string };
    const track = effectiveTrack(s);
    const applications: PermitApplication[] = [];
    const answers: PermitMap['answers'] = [];
    for (const q of jr.questions ?? []) {
        if (!(q.tracks as string[]).includes(track)) continue;
        let items: [string, string][] = [];
        if (q.scope_key === 'structures') {
            items = q.rule === 'one_group'
                ? (s.structures.length ? [['dwelling-group', s.structures.map(x => x.name).join(', ')]] : [])
                : s.structures.map(x => [x.id, x.name] as [string, string]);
        } else if (q.scope_key === 'site_work' && q.rule === 'one_if_true') {
            items = s.site_work ? [['site-work', 'grading, paving, utilities']] : [];
        } else if (q.scope_key === 'demolitions') {
            items = s.demolitions.map((d, i) => [`demo-${i + 1}`, d] as [string, string]);
        } else if (q.scope_key === 'adus') {
            items = s.adus.map((a, i) => [`adu-${i + 1}`, a] as [string, string]);
        }
        const yes = q.id === 'structures' && q.rule === 'one_per_item' ? items.length >= 2 : items.length > 0;
        answers.push({ id: q.id, question: q.question, answer: yes ? 'yes' : 'no', yields: items.length, note: q.note });
        for (const [id, subject] of items) {
            applications.push({ id, kind: q.id, label: q.label, subject, domain: q.scope_key === 'site_work' ? 'landscape' : 'building', sheet_profile: s.jurisdiction, number: 0, title: '' });
        }
    }
    if (!(jr.questions ?? []).length) {
        applications.push({ id: 'project', kind: 'project', label: 'Project', subject: s.project_name || 'Project', domain: 'building', sheet_profile: s.jurisdiction, number: 0, title: '' });
    }
    applications.forEach((a, i) => { a.number = i + 1; a.title = `${a.label}: ${a.subject}`; });
    const n = applications.length;
    return {
        jurisdiction: s.jurisdiction, jurisdiction_title: jr.title, source: jr.source, track,
        application_type: n > 1 ? jr.application_types.multiple : jr.application_types.single,
        application_count: n, applications, answers, warning: jr.warning, contacts: jr.contacts ?? [],
    };
}

export function loadSetup(): ProjectSetup {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return { ...emptySetup(), ...(JSON.parse(raw) as ProjectSetup) };
    } catch { /* private window or blocked storage: start fresh */ }
    return emptySetup();
}

export function saveSetup(s: ProjectSetup): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* per-viewer convenience only */ }
    window.dispatchEvent(new CustomEvent(SETUP_EVENT, { detail: s }));
}

export function clearSetup(): void {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent(SETUP_EVENT, { detail: emptySetup() }));
}

/** Pick the inherited fields out of a facility reference payload (whatever the node currently emits). */
export function inheritFromReference(facilityId: string, ref: Record<string, any>): Inherited {
    const f = (ref.facility ?? {}) as Record<string, any>;
    const pick = <T,>(k: string): T | null => (f[k] === undefined ? null : (f[k] as T));
    return {
        facility_id: facilityId,
        moku_id: ref.moku_id ?? null,
        h3_index: ref.h3_index ?? null,
        facility_name: pick<string>('name'),
        facility_type: pick<string>('facility_type'),
        address: pick<string>('address'),
        latitude: pick<number>('coordinates_lat'),
        longitude: pick<number>('coordinates_lng'),
        structural_system: pick<string>('structural_system'),
        siting_verdict: pick<string>('siting_verdict'),
        siting_class: pick<string>('siting_class'),
        siting_state_code: pick<string>('siting_state_code'),
        siting_county_codes: (f.siting_county_codes as string[] | undefined) ?? [],
        siting_in_corridor: pick<boolean>('siting_in_corridor'),
        ial_priority_eligible: pick<boolean>('ial_priority_eligible'),
        siting_exception: pick<string>('siting_exception'),
        source: 'mokulearner-node services.moku.reference',
    };
}
